export const CHARTER_SECTION_LABELS = {
  scope_objectives: 'Scope & objectives',
  timeline_budget: 'Timeline & budget',
  stakeholders: 'Stakeholders',
  constraints_assumptions: 'Constraints & assumptions',
  success_criteria: 'Success criteria',
};

export const CHARTER_SECTION_ORDER = [
  'scope_objectives',
  'timeline_budget',
  'stakeholders',
  'constraints_assumptions',
  'success_criteria',
];

export function defaultCharterSectionContent(sectionKey) {
  switch (sectionKey) {
    case 'scope_objectives':
      return {
        functional_scope: '',
        geographic_scope: '',
        platform: '',
        strategic_objectives: '',
      };
    case 'timeline_budget':
      return {
        start_date: '',
        go_live_wave1: '',
        go_live_wave2: '',
        total_budget: '',
        external_budget: '',
        internal_budget: '',
      };
    case 'stakeholders':
      return { stakeholders: [] };
    case 'constraints_assumptions':
      return { constraints: [], key_assumptions: '' };
    case 'success_criteria':
      return {
        ootb_target: '',
        go_live_date: '',
        budget_tolerance: '',
        month_end_close: '',
      };
    default:
      return {};
  }
}

/** Field definitions for rendering charter section forms. */
export const CHARTER_SECTION_FIELDS = {
  scope_objectives: [
    { key: 'functional_scope', label: 'Functional scope', multiline: true },
    { key: 'geographic_scope', label: 'Geographic scope' },
    { key: 'platform', label: 'Platform', multiline: true },
    { key: 'strategic_objectives', label: 'Strategic objectives', multiline: true },
  ],
  timeline_budget: [
    { key: 'start_date', label: 'Start date' },
    { key: 'go_live_wave1', label: 'Go-live (Wave 1)' },
    { key: 'go_live_wave2', label: 'Go-live (Wave 2)' },
    { key: 'total_budget', label: 'Total budget' },
    { key: 'external_budget', label: 'External (SI + licences)' },
    { key: 'internal_budget', label: 'Internal' },
  ],
  constraints_assumptions: [
    { key: 'constraints', label: 'Constraints (one per line)', multiline: true, lines: true },
    { key: 'key_assumptions', label: 'Key assumptions', multiline: true },
  ],
  success_criteria: [
    { key: 'ootb_target', label: 'OOTB score target' },
    { key: 'go_live_date', label: 'Go-live date' },
    { key: 'budget_tolerance', label: 'Budget tolerance' },
    { key: 'month_end_close', label: 'Month-end close' },
  ],
};

export const CHARTER_STATUS_LABEL = {
  empty: 'Empty',
  draft: 'Draft',
  review: 'In review',
  approved: 'Approved',
};

/** programme = one per programme; project = one per project within programme */
export const CHARTER_SCOPE = {
  programme: 'programme',
  project: 'project',
};

export function charterScopeTitle(scope) {
  return scope === CHARTER_SCOPE.project ? 'Project charter' : 'Programme charter';
}

export function defaultCharterTitle(scope, { programmeName, projectName } = {}) {
  if (scope === CHARTER_SCOPE.project) return projectName || 'Project charter';
  return programmeName || 'Programme charter';
}

export const SCOPE_PIN_STATUS_LABEL = {
  empty: 'Empty',
  draft: 'Draft',
  review: 'In review',
  approved: 'Approved',
};

export function statusBadgeClass(status) {
  if (status === 'approved' || status === 'accepted' || status === 'baselined') return 'gr';
  if (status === 'review' || status === 'bpo_review' || status === 'sa_sizing' || status === 'flagged') return 'am';
  if (status === 'draft' || status === 'in_progress' || status === 'generated' || status === 'bpo_accepted' || status === 'sized') return 'pu';
  return '';
}

// ── IN-02 Requirements ─────────────────────────────────────────

export const REQ_STATUS_LABEL = {
  draft: 'Draft',
  generated: 'Generated',
  bpo_review: 'BPO review',
  bpo_accepted: 'BPO accepted',
  sa_sizing: 'SA sizing',
  sized: 'Sized',
  approved: 'Approved',
  baselined: 'Baselined',
};

export const REQ_STATUS_ORDER = [
  'draft',
  'generated',
  'bpo_review',
  'bpo_accepted',
  'sa_sizing',
  'sized',
  'approved',
  'baselined',
];

export const OOTB_CLASSIFICATION_LABEL = {
  ootb: 'OOTB',
  config: 'Config',
  enhancement: 'Enhancement',
  custom_dev: 'Custom dev',
};

export const OOTB_BADGE_VARIANT = {
  ootb: 'gr',
  config: 'pu',
  enhancement: 'am',
  custom_dev: 'rd',
};

export const REQUIREMENT_TYPE_LABEL = {
  standard: 'Standard',
  configuration: 'Configuration',
  regulatory: 'Regulatory',
  complexity: 'Complexity',
};

export const NODE_REVIEW_STATUS_LABEL = {
  pending: 'Pending',
  review: 'In review',
  accepted: 'Accepted',
  flagged: 'Flagged',
};

// ── Solution options / bake-off (IN-03/IN-04) ───────────────────

export const OPTION_STATUS_LABEL = {
  draft: 'Draft',
  in_assessment: 'In assessment',
  assessed: 'Assessed',
  selected: 'Selected',
  rejected: 'Rejected',
};

export const MAX_SOLUTION_OPTIONS = 3;

export const DEFAULT_SCORING_WEIGHTS = {
  ootb_fit: { label: 'OOTB score potential', weight: 3 },
  functional_coverage: { label: 'Functional coverage', weight: 3 },
  affordability: { label: 'Affordability vs budget', weight: 2 },
  integration: { label: 'Integration complexity', weight: 3 },
};

export const T_SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL'];

export const ARCHITECTURE_TABS = [
  { key: 'narrative', label: 'Architecture narrative' },
  { key: 'integration', label: 'Integration diagram' },
  { key: 'process', label: 'Process flows' },
];

export const ARCHITECTURE_NARRATIVE_SECTIONS = [
  { key: 'selected_platforms', label: 'Selected platforms' },
  { key: 'deployment_model', label: 'Deployment model' },
  { key: 'integration_approach', label: 'Integration approach' },
  { key: 'data_migration', label: 'Data migration approach' },
  { key: 'phasing', label: 'Phasing' },
];

export const ARCHITECTURE_DECISION_IMPACT_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const ARCHITECTURE_DECISION_STATUS_LABEL = {
  proposed: 'Proposed',
  under_review: 'Under review',
  confirmed: 'Confirmed',
};

export const INTERFACE_TYPE_LABEL = {
  batch: 'Batch',
  real_time: 'Real-time',
  complex_event: 'Complex event',
};

export function defaultArchitectureNarrative() {
  return {
    selected_platforms: '',
    deployment_model: '',
    integration_approach: '',
    data_migration: '',
    phasing: '',
  };
}

export function defaultProcessFlows() {
  return {
    active_flow_id: 'gl',
    flows: [
      { id: 'gl', domain: 'ERP · Finance', label: 'General Ledger', summary: '', lanes: [] },
      { id: 'ap', domain: 'ERP · Finance', label: 'Accounts Payable', summary: '', lanes: [] },
      { id: 'ar', domain: 'ERP · Finance', label: 'Accounts Receivable', summary: '', lanes: [] },
      { id: 'pr', domain: 'ERP · Procurement', label: 'Purchase to Pay', summary: '', lanes: [] },
      { id: 'ec', domain: 'HCM', label: 'Employee Central', summary: '', lanes: [] },
    ],
  };
}

export function defaultIntegrationShape() {
  return {
    core_platforms: [],
    middleware_note: '',
    external_systems: [],
    legacy_systems: [],
  };
}

// ── IN-05 ROM estimate ─────────────────────────────────────────

export const ROM_TABS = [
  { key: 'summary', label: 'Summary & PERT' },
  { key: 'build', label: 'Build breakdown' },
  { key: 'assumptions', label: 'Assumptions & overrides' },
];

export const ROM_STATUS_LABEL = {
  empty: 'Not calculated',
  draft: 'Draft',
  review: 'In review',
  approved: 'Approved',
};

/** Next status for bulk workflow actions. */
export function nextBulkWorkflowStatus(currentCounts) {
  if (currentCounts.generated > 0) return 'bpo_review';
  if (currentCounts.bpo_review > 0) return 'bpo_accepted';
  if (currentCounts.bpo_accepted > 0) return 'sa_sizing';
  if (currentCounts.sa_sizing > 0) return 'sized';
  if (currentCounts.sized > 0) return 'approved';
  return null;
}
